import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Input,
  InputNumber,
  Select,
  Spin,
  Table,
  Tag,
  notification,
} from "antd";
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import SmartRtpLayout from "./components/SmartRtpLayout";
import { usePermissions } from "../../../controllers/PermissionContext";
import {
  getGamesList,
  getSlabById,
  getSlabsBoard,
  saveSlabsBoard,
} from "../../../controllers/V1/smartRtp/smartRtpController";
import { formatSmartRtpNumber } from "../../../utils/bigMath";

const SLOT_COUNT = 20;

const DEFAULT_AMOUNTS = [
  1, 2, 3, 4, 5, 6, 7, 10, 14, 15, 16, 20, 22, 25, 30, 35, 40, 50, 75, 100,
];

function categoryForSlot(slot) {
  if (slot <= 6) return "Low";
  if (slot <= 14) return "Medium";
  return "High";
}

function emptySlot(slot) {
  const prize_category = categoryForSlot(slot);
  return {
    editId: null,
    slot_index: slot,
    slab_code: String(slot),
    slab_name: `Slot ${slot}`,
    prize_category,
    prize_type: "Fixed",
    fixed_amount: DEFAULT_AMOUNTS[slot - 1] || slot,
    pool_share_percent: 0,
    base_selection_weight: 5,
    is_big_prize_candidate: prize_category === "High",
    max_winners_per_draw: 0,
    max_winners_per_hour: 0,
    min_buyer_score: 0,
    status: "A",
  };
}

function buildDefaultBoard() {
  return Array.from({ length: SLOT_COUNT }, (_, i) => emptySlot(i + 1));
}

/** Convert legacy relative weights to percentages when board does not already sum to ~100. */
function normalizeWeightsToPercent(rows = []) {
  const list = rows || [];
  const active = list.filter((r) => (r.status || "A") === "A");
  const sum = active.reduce((s, r) => s + Number(r.base_selection_weight || 0), 0);
  if (active.length === 0) return list;
  if (sum > 0 && Math.abs(sum - 100) <= 0.05) return list;

  if (sum <= 0) {
    const equal = Math.round((100 / active.length) * 1000) / 1000;
    return list.map((r) => ({
      ...r,
      base_selection_weight: (r.status || "A") === "A" ? equal : 0,
    }));
  }

  return list.map((r) => {
    if ((r.status || "A") !== "A") {
      return { ...r, base_selection_weight: Number(r.base_selection_weight) || 0 };
    }
    const pct = (Number(r.base_selection_weight || 0) / sum) * 100;
    return {
      ...r,
      base_selection_weight: Math.round(pct * 1000) / 1000,
    };
  });
}

function mergeBoard(existingRows = []) {
  const bySlot = new Map(
    (existingRows || []).map((row) => [Number(row.slot_index), row])
  );
  const merged = Array.from({ length: SLOT_COUNT }, (_, i) => {
    const slot = i + 1;
    const row = bySlot.get(slot);
    if (!row) return emptySlot(slot);
    const prize_category = row.prize_category || categoryForSlot(slot);
    return {
      editId: row._id || null,
      slot_index: slot,
      slab_code: row.slab_code || String(slot),
      slab_name: row.slab_name || `Slot ${slot}`,
      prize_category,
      prize_type: row.prize_type || "Fixed",
      fixed_amount: Number(row.fixed_amount ?? DEFAULT_AMOUNTS[slot - 1] ?? 0),
      pool_share_percent: Number(row.pool_share_percent || 0),
      base_selection_weight: Number(row.base_selection_weight ?? 5),
      is_big_prize_candidate:
        row.is_big_prize_candidate === true || prize_category === "High",
      max_winners_per_draw: Number(row.max_winners_per_draw || 0),
      max_winners_per_hour: Number(row.max_winners_per_hour || 0),
      min_buyer_score: Number(row.min_buyer_score || 0),
      status: row.status || "A",
    };
  });
  return normalizeWeightsToPercent(merged);
}

const CATEGORY_STYLE = {
  Low: {
    tag: "green",
    color: "#198754",
    bg: "#e8f5ee",
    inactiveBg: "#d5dcd8",
    hoverBg: "#d7eee2",
    inactiveHoverBg: "#c5cec9",
  },
  Medium: {
    tag: "orange",
    color: "#fd7e14",
    bg: "#fff3e8",
    inactiveBg: "#e8e2db",
    hoverBg: "#ffe4cc",
    inactiveHoverBg: "#d9d1c8",
  },
  High: {
    tag: "red",
    color: "#dc3545",
    bg: "#fdecee",
    inactiveBg: "#e6dcde",
    hoverBg: "#f8d7db",
    inactiveHoverBg: "#d6c8cb",
  },
};

function categoryColor(cat) {
  return (CATEGORY_STYLE[cat] || CATEGORY_STYLE.Low).tag;
}

function slabRowClassName(row) {
  const cat = row.prize_category === "Medium" || row.prize_category === "High"
    ? row.prize_category
    : "Low";
  const inactive = (row.status || "A") !== "A";
  return `slab-row-${cat}${inactive ? " slab-row-inactive" : ""}`;
}

const SLAB_TABLE_ROW_CSS = `
.smart-rtp-slab-table .ant-table-tbody > tr.slab-row-Low > td { background: ${CATEGORY_STYLE.Low.bg} !important; }
.smart-rtp-slab-table .ant-table-tbody > tr.slab-row-Medium > td { background: ${CATEGORY_STYLE.Medium.bg} !important; }
.smart-rtp-slab-table .ant-table-tbody > tr.slab-row-High > td { background: ${CATEGORY_STYLE.High.bg} !important; }
.smart-rtp-slab-table .ant-table-tbody > tr.slab-row-Low.slab-row-inactive > td { background: ${CATEGORY_STYLE.Low.inactiveBg} !important; color: #6c757d; }
.smart-rtp-slab-table .ant-table-tbody > tr.slab-row-Medium.slab-row-inactive > td { background: ${CATEGORY_STYLE.Medium.inactiveBg} !important; color: #6c757d; }
.smart-rtp-slab-table .ant-table-tbody > tr.slab-row-High.slab-row-inactive > td { background: ${CATEGORY_STYLE.High.inactiveBg} !important; color: #6c757d; }
.smart-rtp-slab-table .ant-table-tbody > tr.slab-row-Low:hover > td { background: ${CATEGORY_STYLE.Low.hoverBg} !important; }
.smart-rtp-slab-table .ant-table-tbody > tr.slab-row-Medium:hover > td { background: ${CATEGORY_STYLE.Medium.hoverBg} !important; }
.smart-rtp-slab-table .ant-table-tbody > tr.slab-row-High:hover > td { background: ${CATEGORY_STYLE.High.hoverBg} !important; }
.smart-rtp-slab-table .ant-table-tbody > tr.slab-row-Low.slab-row-inactive:hover > td { background: ${CATEGORY_STYLE.Low.inactiveHoverBg} !important; }
.smart-rtp-slab-table .ant-table-tbody > tr.slab-row-Medium.slab-row-inactive:hover > td { background: ${CATEGORY_STYLE.Medium.inactiveHoverBg} !important; }
.smart-rtp-slab-table .ant-table-tbody > tr.slab-row-High.slab-row-inactive:hover > td { background: ${CATEGORY_STYLE.High.inactiveHoverBg} !important; }
`;

export default function SmartRtpSlabFormPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const PERMISSION = usePermissions("smartRtp");
  const canEdit =
    PERMISSION?.fullAccess === "Y" || PERMISSION?.addEdit === "Y";

  const editId = searchParams.get("editId");
  const initialGameId = searchParams.get("gameId") || "";
  const backTo = "/smart-rtp/games";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [gameOptions, setGameOptions] = useState([
    { label: "Global (all games)", value: "" },
  ]);
  const [gameId, setGameId] = useState(initialGameId);
  const [rows, setRows] = useState(buildDefaultBoard());

  useEffect(() => {
    if (
      PERMISSION &&
      !(
        PERMISSION.fullAccess === "Y" ||
        PERMISSION.addEdit === "Y" ||
        PERMISSION.list === "Y" ||
        PERMISSION.view === "Y"
      )
    ) {
      navigate("/dashboard");
    }
  }, [PERMISSION, navigate]);

  useEffect(() => {
    document.title = "Crown King || Smart RTP Prize Board";
  }, []);

  useEffect(() => {
    let cancelled = false;
    const boot = async () => {
      setLoading(true);
      try {
        const gamesRes = await getGamesList({
          type: "",
          condition: {},
          limit: 0,
          skip: 0,
          sort: { title: 1 },
        });
        if (!cancelled && gamesRes.status === true) {
          setGameOptions([
            { label: "Global (all games)", value: "" },
            ...(gamesRes.result || []).map((item) => ({
              label: `${item.title}${item.game_code ? ` (${item.game_code})` : ""}`,
              value: String(item._id),
            })),
          ]);
        }

        let scopeGameId = initialGameId;
        if (editId) {
          const one = await getSlabById(editId);
          if (one?.status === true && one.result) {
            const gid = one.result.game_id;
            scopeGameId =
              gid && typeof gid === "object"
                ? String(gid._id || "")
                : gid
                  ? String(gid)
                  : "";
          }
        }

        if (!cancelled) setGameId(scopeGameId || "");
        const boardRes = await getSlabsBoard({
          game_id: scopeGameId || null,
        });
        if (!cancelled) {
          if (boardRes.status === true) {
            setRows(mergeBoard(boardRes.result || []));
          } else {
            setRows(buildDefaultBoard());
          }
        }
      } catch (error) {
        if (!cancelled) {
          setRows(buildDefaultBoard());
          notification.error({
            message: "Error",
            description: "Unable to load prize board.",
            icon: <InfoCircleOutlined style={{ color: "red" }} />,
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    boot();
    return () => {
      cancelled = true;
    };
  }, [editId, initialGameId]);

  const loadBoardForGame = async (nextGameId) => {
    setLoading(true);
    setGameId(nextGameId);
    try {
      const boardRes = await getSlabsBoard({
        game_id: nextGameId || null,
      });
      if (boardRes.status === true) {
        setRows(mergeBoard(boardRes.result || []));
      } else {
        setRows(buildDefaultBoard());
      }
    } catch (error) {
      setRows(buildDefaultBoard());
    } finally {
      setLoading(false);
    }
  };

  const updateRow = (slotIndex, patch) => {
    setRows((prev) =>
      prev.map((row) =>
        row.slot_index === slotIndex
          ? {
              ...row,
              ...patch,
              is_big_prize_candidate:
                patch.prize_category === "High"
                  ? true
                  : patch.prize_category
                    ? false
                    : patch.is_big_prize_candidate ?? row.is_big_prize_candidate,
            }
          : row
      )
    );
  };

  const fillDefaults = () => {
    setRows((prev) =>
      prev.map((row) => {
        const defaults = emptySlot(row.slot_index);
        return {
          ...defaults,
          editId: row.editId,
        };
      })
    );
  };

  const filledCount = useMemo(
    () => rows.filter((r) => Number(r.fixed_amount) > 0 || r.prize_type !== "Fixed").length,
    [rows]
  );

  const activeWeightSum = useMemo(
    () =>
      rows
        .filter((r) => (r.status || "A") === "A")
        .reduce((sum, r) => sum + Number(r.base_selection_weight || 0), 0),
    [rows]
  );
  const weightSumOk = Math.abs(activeWeightSum - 100) <= 0.05;

  const categorySummary = useMemo(() => {
    const totals = {
      Low: { weight: 0, prize: 0 },
      Medium: { weight: 0, prize: 0 },
      High: { weight: 0, prize: 0 },
    };
    rows
      .filter((r) => (r.status || "A") === "A")
      .forEach((r) => {
        const cat = r.prize_category || "Low";
        if (!totals[cat]) return;
        totals[cat].weight += Number(r.base_selection_weight || 0);
        totals[cat].prize += Number(r.fixed_amount || 0);
      });
    return {
      Low: {
        weight: Math.round(totals.Low.weight * 1000) / 1000,
        prize: Math.round(totals.Low.prize * 1000) / 1000,
      },
      Medium: {
        weight: Math.round(totals.Medium.weight * 1000) / 1000,
        prize: Math.round(totals.Medium.prize * 1000) / 1000,
      },
      High: {
        weight: Math.round(totals.High.weight * 1000) / 1000,
        prize: Math.round(totals.High.prize * 1000) / 1000,
      },
    };
  }, [rows]);

  const handleSave = async () => {
    if (!canEdit) return;
    for (const row of rows) {
      if (!row.slab_code?.trim() || !row.slab_name?.trim()) {
        notification.error({
          message: "Validation",
          description: `Slot ${row.slot_index}: code and name are required.`,
          icon: <InfoCircleOutlined style={{ color: "red" }} />,
        });
        return;
      }
      const w = Number(row.base_selection_weight);
      if (!Number.isFinite(w) || w < 0 || w > 100) {
        notification.error({
          message: "Validation",
          description: `Slot ${row.slot_index}: Weight % must be between 0 and 100.`,
          icon: <InfoCircleOutlined style={{ color: "red" }} />,
        });
        return;
      }
    }
    if (!weightSumOk) {
      notification.error({
        message: "Validation",
        description: `Active slot Weight % must sum to 100 (currently ${activeWeightSum.toFixed(3)}).`,
        icon: <InfoCircleOutlined style={{ color: "red" }} />,
      });
      return;
    }

    setSaving(true);
    const res = await saveSlabsBoard({
      game_id: gameId || null,
      slabs: rows.map((row) => ({
        ...(row.editId ? { editId: row.editId } : {}),
        slot_index: row.slot_index,
        slab_code: row.slab_code,
        slab_name: row.slab_name,
        prize_category: row.prize_category,
        prize_type: row.prize_type,
        fixed_amount: Number(row.fixed_amount || 0),
        pool_share_percent: Number(row.pool_share_percent || 0),
        base_selection_weight: Number(row.base_selection_weight ?? 5),
        is_big_prize_candidate: !!row.is_big_prize_candidate,
        max_winners_per_draw: Number(row.max_winners_per_draw || 0),
        max_winners_per_hour: Number(row.max_winners_per_hour || 0),
        min_buyer_score: Number(row.min_buyer_score || 0),
        status: row.status || "A",
      })),
    });
    setSaving(false);

    if (res.status === true) {
      notification.success({
        message: "Success",
        description: `Saved ${res.count || rows.length} prize slots.`,
        icon: <CheckCircleOutlined style={{ color: "green" }} />,
        duration: 2,
      });
      const saved = res.result || [];
      if (saved.length) setRows(mergeBoard(saved));
      navigate(backTo);
      return;
    }

    notification.error({
      message: "Error",
      description: res.message || "Unable to save prize board.",
      icon: <InfoCircleOutlined style={{ color: "red" }} />,
    });
  };

  const columns = [
    {
      title: "Slot",
      dataIndex: "slot_index",
      width: 70,
      fixed: "left",
      render: (v, row) => (
        <Tag color={categoryColor(row.prize_category)}>#{v}</Tag>
      ),
    },
    {
      title: "Category",
      dataIndex: "prize_category",
      width: 120,
      render: (v, row) => (
        <Select
          value={v}
          style={{ width: "100%" }}
          disabled={!canEdit}
          onChange={(prize_category) => updateRow(row.slot_index, { prize_category })}
          options={[
            { label: "Low", value: "Low" },
            { label: "Medium", value: "Medium" },
            { label: "High", value: "High" },
          ]}
        />
      ),
    },
    {
      title: "Code",
      dataIndex: "slab_code",
      width: 110,
      render: (v, row) => (
        <Input
          value={v}
          disabled={!canEdit}
          onChange={(e) => updateRow(row.slot_index, { slab_code: e.target.value })}
        />
      ),
    },
    {
      title: "Name",
      dataIndex: "slab_name",
      width: 160,
      render: (v, row) => (
        <Input
          value={v}
          disabled={!canEdit}
          onChange={(e) => updateRow(row.slot_index, { slab_name: e.target.value })}
        />
      ),
    },
    {
      title: "Type",
      dataIndex: "prize_type",
      width: 120,
      render: (v, row) => (
        <Select
          value={v}
          style={{ width: "100%" }}
          disabled={!canEdit}
          onChange={(prize_type) => updateRow(row.slot_index, { prize_type })}
          options={[
            { label: "Fixed", value: "Fixed" },
            { label: "Pool Share", value: "PoolShare" },
            { label: "Manual", value: "Manual" },
          ]}
        />
      ),
    },
    {
      title: "Prize Amount",
      dataIndex: "fixed_amount",
      width: 120,
      render: (v, row) => (
        <InputNumber
          value={v}
          min={0}
          style={{ width: "100%" }}
          disabled={!canEdit}
          onChange={(fixed_amount) =>
            updateRow(row.slot_index, { fixed_amount: fixed_amount ?? 0 })
          }
        />
      ),
    },
    {
      title: "Weight %",
      dataIndex: "base_selection_weight",
      width: 110,
      render: (v, row) => (
        <InputNumber
          value={v}
          min={0}
          max={100}
          step={0.001}
          precision={3}
          addonAfter="%"
          style={{ width: "100%" }}
          disabled={!canEdit}
          onChange={(base_selection_weight) =>
            updateRow(row.slot_index, {
              base_selection_weight:
                base_selection_weight == null ? 0 : base_selection_weight,
            })
          }
        />
      ),
    },
    {
      title: "Big Prize",
      dataIndex: "is_big_prize_candidate",
      width: 100,
      render: (v, row) => (
        <Select
          value={!!v}
          style={{ width: "100%" }}
          disabled={!canEdit}
          onChange={(is_big_prize_candidate) =>
            updateRow(row.slot_index, { is_big_prize_candidate })
          }
          options={[
            { label: "No", value: false },
            { label: "Yes", value: true },
          ]}
        />
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 110,
      render: (v, row) => (
        <Select
          value={v}
          style={{ width: "100%" }}
          disabled={!canEdit}
          onChange={(status) => updateRow(row.slot_index, { status })}
          options={[
            { label: "Active", value: "A" },
            { label: "Inactive", value: "I" },
          ]}
        />
      ),
    },
  ];

  return (
    <SmartRtpLayout
      title="Smart RTP Prize Board (20 slots)"
      activeKey="games"
      headerVariant="simple"
      actions={
        <Link to={backTo}>
          <Button type="secondary">
            <ArrowLeftOutlined /> Back
          </Button>
        </Link>
      }
    >
      <div className="card shadow mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-end mb-3">
            <div className="col-md-5">
              <label className="form-label fw-semibold">Game / Scope</label>
              <Select
                showSearch
                optionFilterProp="label"
                style={{ width: "100%" }}
                value={gameId}
                disabled={loading}
                options={gameOptions}
                onChange={loadBoardForGame}
              />
            </div>
            <div className="col-md-7 d-flex flex-wrap gap-2 justify-content-md-end">
              <Button onClick={fillDefaults} disabled={loading || !canEdit}>
                Fill default 20 prizes
              </Button>
              <Button
                type="primary"
                loading={saving}
                disabled={loading || !canEdit}
                onClick={handleSave}
              >
                Save all 20 slots
              </Button>
            </div>
          </div>

          <div className="mb-3 text-muted">
            Edit all prize slots on one page.{" "}
            <strong>{filledCount}/{SLOT_COUNT}</strong> slots have a prize amount.
            Low = consolation, Medium = mid, High = big prizes.
            {" · "}
            Active Weight %{" "}
            <strong style={{ color: weightSumOk ? "#198754" : "#dc3545" }}>
              {activeWeightSum.toFixed(3)}
            </strong>
            /100
            {!weightSumOk ? " (must equal 100 to save)" : ""}
          </div>

          <div
            className="mb-3"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
              gap: 12,
            }}
          >
            {["Low", "Medium", "High"].map((key) => {
              const item = CATEGORY_STYLE[key];
              return (
                <div
                  key={key}
                  style={{
                    background: item.bg,
                    border: `1px solid ${item.color}33`,
                    borderLeft: `4px solid ${item.color}`,
                    borderRadius: 8,
                    padding: "12px 14px",
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      letterSpacing: 0.6,
                      color: "#6c757d",
                      marginBottom: 4,
                    }}
                  >
                    {key.toUpperCase()}
                  </div>
                  <div
                    style={{
                      fontSize: 22,
                      fontWeight: 700,
                      color: item.color,
                      lineHeight: 1.2,
                    }}
                  >
                    {Number(categorySummary[key]?.weight || 0).toFixed(3)}%
                  </div>
                  <div
                    style={{
                      marginTop: 6,
                      fontSize: 13,
                      color: "#495057",
                      fontWeight: 600,
                    }}
                  >
                    Prize Amount:{" "}
                    <span style={{ color: item.color }}>
                      {formatSmartRtpNumber(categorySummary[key]?.prize || 0)} QR
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {loading ? (
            <div className="text-center p-5">
              <Spin />
            </div>
          ) : (
            <div className="smart-rtp-slab-table">
              <style>{SLAB_TABLE_ROW_CSS}</style>
              <Table
                size="small"
                pagination={false}
                rowKey="slot_index"
                rowClassName={slabRowClassName}
                columns={columns}
                dataSource={rows}
                scroll={{ x: 1100 }}
                bordered
              />
            </div>
          )}
        </div>
      </div>
    </SmartRtpLayout>
  );
}
